import { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import {
  Description,
  Settings,
  LocalOffer,
  Timeline,
  Map,
  AdminPanelSettings,
  SupervisorAccount,
  BugReport,
  Visibility,
  PersonAdd,
  Save,
  Publish,
  ArrowDropDown,
  Delete,
  Image,
  History,
  Groups,
  QrCode2,
  Folder,
  CheckCircle,
  Cancel,
  VerifiedUser,
  Upload,
  Lightbulb,
} from '@mui/icons-material'
import './styles/AdventureRibbon.css'
import { usePublishedPermissions } from '../../hooks/usePublishedPermissions'
import { usePublishReadiness } from '../../hooks/usePublishReadiness'

// ====================
// TYPES
// ====================

export type SectionId =
  | 'description'
  | 'settings'
  | 'groups'
  | 'tags'
  | 'images'
  | 'files'
  | 'locationflow'
  | 'mapview'
  | 'codes'
  | 'maintainers'
  | 'moderators'
  | 'testers'
  | 'clues'
  | 'display'
  | 'join'

// Valid section IDs for URL hash navigation
export const VALID_SECTION_IDS: SectionId[] = [
  'description',
  'settings',
  'groups',
  'tags',
  'images',
  'files',
  'locationflow',
  'mapview',
  'codes',
  'clues',
  'maintainers',
  'moderators',
  'testers',
  'display',
  'join',
]

interface RibbonSection {
  header: string
  items: RibbonItem[]
}

interface RibbonItem {
  id: SectionId | 'save' | 'publish' | 'delete' | 'history' | 'flowhistory' | 'flowimport'
  label: string
  icon: React.ReactNode
  isAction?: boolean
  disabled?: boolean
  color?: 'error' | 'primary' | 'secondary'
  dividerBefore?: boolean
}

interface AdventureRibbonProps {
  activeSection: SectionId
  onSectionChanged: (section: SectionId) => void
  onSave?: () => void
  onPublish?: () => void
  onDelete?: () => void
  onHistory?: () => void
  onFlowVersionHistory?: () => void
  onImportFlow?: () => void
  isSaving?: boolean
  isPublishing?: boolean
  isReadyToPublish?: boolean
  onExport?: () => void
  onImport?: () => void
  hiddenSections?: SectionId[]
  workflowAllowed?: boolean
}

// ====================
// CONFIGURATION
// ====================

export const ribbonSections: RibbonSection[] = [
  {
    header: 'Settings',
    items: [
      { id: 'description', label: 'Description', icon: <Description fontSize="small" /> },
      { id: 'images', label: 'Images', icon: <Image fontSize="small" /> },
      { id: 'settings', label: 'Settings', icon: <Settings fontSize="small" /> },
      { id: 'groups', label: 'Groups', icon: <Groups fontSize="small" /> },
      { id: 'tags', label: 'Tags', icon: <LocalOffer fontSize="small" /> },
      { id: 'files', label: 'Files', icon: <Folder fontSize="small" /> },
      { id: 'codes', label: 'Codes', icon: <QrCode2 fontSize="small" /> },
    ],
  },
  {
    header: 'Flow',
    items: [
      { id: 'locationflow', label: 'Node Editor', icon: <Timeline fontSize="small" /> },
      { id: 'mapview', label: 'Map View', icon: <Map fontSize="small" /> },
      { id: 'clues', label: 'Clues', icon: <Lightbulb fontSize="small" /> },
      { id: 'flowimport', label: 'Import JSON', icon: <Upload fontSize="small" />, isAction: true, dividerBefore: true },
      { id: 'flowhistory', label: 'Version History', icon: <History fontSize="small" />, isAction: true },
    ],
  },
  {
    header: 'Team',
    items: [
      { id: 'maintainers', label: 'Maintainers', icon: <AdminPanelSettings fontSize="small" /> },
      { id: 'moderators', label: 'Moderators', icon: <SupervisorAccount fontSize="small" /> },
      { id: 'testers', label: 'Testers', icon: <BugReport fontSize="small" /> },
    ],
  },
  {
    header: 'Criteria',
    items: [
      { id: 'display', label: 'Display', icon: <Visibility fontSize="small" /> },
      { id: 'join', label: 'Join', icon: <PersonAdd fontSize="small" /> },
    ],
  },
  {
    header: 'Actions',
    items: [
      { id: 'save', label: 'Save', icon: <Save fontSize="small" />, isAction: true },
      { id: 'publish', label: 'Publish', icon: <Publish fontSize="small" />, isAction: true },
      { id: 'history', label: 'History', icon: <History fontSize="small" />, isAction: true },
      { id: 'delete', label: 'Delete', icon: <Delete fontSize="small" />, isAction: true, color: 'error' },
    ],
  },
]

// ====================
// SECTION DROPDOWN COMPONENT
// ====================

interface SectionDropdownProps {
  section: RibbonSection
  activeSection: SectionId
  onSectionChanged: (section: SectionId) => void
  onSave?: () => void
  onPublish?: () => void
  onDelete?: () => void
  onHistory?: () => void
  onFlowVersionHistory?: () => void
  onImportFlow?: () => void
  isSaving?: boolean
  isPublishing?: boolean
  isReadyToPublish?: boolean
  canSave?: boolean
  canPublish?: boolean
  canDelete?: boolean
  isPublished?: boolean
  hiddenSections?: SectionId[]
  publishBlockers?: string[]
  workflowAllowed?: boolean
}

function SectionDropdown({
  section,
  activeSection,
  onSectionChanged,
  onSave,
  onPublish,
  onDelete,
  onHistory,
  onFlowVersionHistory,
  onImportFlow,
  isSaving,
  isPublishing,
  isReadyToPublish,
  canSave = true,
  canPublish = true,
  canDelete = true,
  isPublished = false,
  hiddenSections = [],
  publishBlockers = [],
  workflowAllowed = true,
}: SectionDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (item: RibbonItem) => {
    if (item.isAction) {
      if (item.id === 'save' && onSave) {
        onSave()
      } else if (item.id === 'publish' && onPublish) {
        onPublish()
      } else if (item.id === 'delete' && onDelete) {
        onDelete()
      } else if (item.id === 'history' && onHistory) {
        onHistory()
      } else if (item.id === 'flowhistory' && onFlowVersionHistory) {
        onFlowVersionHistory()
      } else if (item.id === 'flowimport' && onImportFlow) {
        onImportFlow()
      }
    } else {
      onSectionChanged(item.id as SectionId)
    }
    handleClose()
  }

  const isItemDisabled = (item: RibbonItem): boolean => {
    if (!item.isAction) return false
    if (item.id === 'save') return !canSave || isSaving || isPublishing || false
    if (item.id === 'publish') return !canPublish || !isReadyToPublish || isSaving || isPublishing || false
    if (item.id === 'delete') return !canDelete || false
    return false
  }

  // Filter out items that should be hidden
  const isItemHidden = (item: RibbonItem): boolean => {
    // Check if this section is in hiddenSections
    if (!item.isAction && hiddenSections.includes(item.id as SectionId)) return true
    // Hide Save when published
    if (item.id === 'save' && !canSave) return true
    // Hide Publish when already published (not just disabled)
    if (item.id === 'publish' && isPublished) return true
    // Hide flow actions when workflow is not allowed
    if ((item.id === 'flowhistory' || item.id === 'flowimport') && !workflowAllowed) return true
    return false
  }

  // Get visible items for this section
  const visibleItems = section.items.filter(item => !isItemHidden(item))

  // Check if any item in this section is active
  const isActive = section.items.some((item) => !item.isAction && item.id === activeSection)

  // Don't render the dropdown if there are no visible items
  if (visibleItems.length === 0) return null

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        variant={isActive ? 'contained' : 'text'}
        size="small"
        sx={{ textTransform: 'none', fontWeight: 500 }}
      >
        {section.header}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {visibleItems.map((item, index) => {
          const disabled = isItemDisabled(item)
          const isLoading = item.id === 'save' ? isSaving : item.id === 'publish' ? isPublishing : false

          // Build tooltip for disabled Publish button
          const showPublishTooltip = item.id === 'publish' && disabled && publishBlockers.length > 0
          const tooltipTitle = showPublishTooltip
            ? `Cannot publish: ${publishBlockers.slice(0, 3).join(', ')}${publishBlockers.length > 3 ? '...' : ''}`
            : ''

          const menuItem = (
            <MenuItem
              key={item.id}
              onClick={() => handleMenuItemClick(item)}
              selected={!item.isAction && activeSection === item.id}
              disabled={disabled}
              sx={item.color === 'error' ? { color: 'error.main' } : undefined}
            >
              <ListItemIcon sx={item.color === 'error' ? { color: 'error.main' } : undefined}>
                {isLoading ? <CircularProgress size={20} /> : item.icon}
              </ListItemIcon>
              <ListItemText>
                {isLoading && item.id === 'save'
                  ? 'Saving...'
                  : isLoading && item.id === 'publish'
                  ? 'Publishing...'
                  : item.label}
              </ListItemText>
            </MenuItem>
          )

          // Build the result with optional divider before
          const result = []
          if (item.dividerBefore && index > 0) {
            result.push(<Divider key={`divider-${item.id}`} />)
          }

          // Wrap in tooltip if publish is disabled with blockers
          if (showPublishTooltip) {
            result.push(
              <Tooltip key={item.id} title={tooltipTitle} placement="right" arrow>
                <span>{menuItem}</span>
              </Tooltip>
            )
          } else {
            result.push(menuItem)
          }

          return result
        })}
      </Menu>
    </>
  )
}

// ====================
// MAIN COMPONENT
// ====================

export function AdventureRibbon({
  activeSection,
  onSectionChanged,
  onSave,
  onPublish,
  onDelete,
  onHistory,
  onFlowVersionHistory,
  onImportFlow,
  isSaving,
  isPublishing,
  isReadyToPublish,
  onExport,
  onImport,
  hiddenSections,
  workflowAllowed = true,
}: AdventureRibbonProps) {
  const [readinessDialogOpen, setReadinessDialogOpen] = useState(false)

  // Get published permissions to control action visibility
  const { canSave, canPublish, canDelete, isPublished } = usePublishedPermissions()
  // Get publish readiness for tooltip feedback and dialog
  const { blockers: publishBlockers, isReady, items } = usePublishReadiness()

  // Determine chip color and label based on status
  const getStatusChip = () => {
    if (isPublished) {
      return { label: 'Published', color: 'success' as const, icon: <VerifiedUser sx={{ fontSize: 16 }} /> }
    }
    if (isReady) {
      return { label: 'Ready', color: 'success' as const, icon: <CheckCircle sx={{ fontSize: 16 }} /> }
    }
    return { label: 'Not Ready', color: 'warning' as const, icon: <Cancel sx={{ fontSize: 16 }} /> }
  }

  const statusChip = getStatusChip()
  const metCount = items.filter((item) => item.met).length

  return (
    <>
      <Paper
        className="compact-ribbon"
        elevation={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#fff',
          borderRadius: 0,
          m: 0,
        }}
      >
        {/* Section Dropdowns */}
        {ribbonSections.map((section) => (
          <SectionDropdown
            key={section.header}
            section={section}
            activeSection={activeSection}
            onSectionChanged={onSectionChanged}
            onSave={onSave}
            onPublish={onPublish}
            onDelete={onDelete}
            onHistory={onHistory}
            onFlowVersionHistory={onFlowVersionHistory}
            onImportFlow={onImportFlow}
            isSaving={isSaving}
            isPublishing={isPublishing}
            isReadyToPublish={isReadyToPublish}
            canSave={canSave}
            canPublish={canPublish}
            canDelete={canDelete}
            isPublished={isPublished}
            hiddenSections={hiddenSections}
            publishBlockers={publishBlockers}
            workflowAllowed={workflowAllowed}
          />
        ))}

        {/* Spacer to push status chip to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Publish Readiness Status Chip */}
        <Tooltip
          title={
            isPublished
              ? 'This adventure is published'
              : isReady
              ? 'Ready to publish - click for details'
              : `${metCount} of ${items.length} requirements met - click for details`
          }
        >
          <Chip
            icon={statusChip.icon}
            label={statusChip.label}
            color={statusChip.color}
            size="small"
            onClick={() => setReadinessDialogOpen(true)}
            sx={{
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          />
        </Tooltip>
      </Paper>

      {/* Publish Readiness Dialog */}
      <Dialog
        open={readinessDialogOpen}
        onClose={() => setReadinessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Publish sx={{ color: isReady ? 'success.main' : 'warning.main' }} />
          Publish Readiness
          <Chip
            size="small"
            label={statusChip.label}
            color={statusChip.color}
            sx={{ ml: 'auto' }}
          />
        </DialogTitle>
        <DialogContent dividers>
          {isPublished ? (
            <Typography color="text.secondary">
              This adventure has already been published.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isReady
                  ? 'All requirements are met. You can publish this adventure from the Actions menu.'
                  : `Complete the following requirements to enable publishing (${metCount} of ${items.length} met):`}
              </Typography>
              <List dense>
                {items.map((item) => (
                  <ListItem key={item.id} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.met ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: item.met ? 400 : 500,
                        color: item.met ? 'text.secondary' : 'text.primary',
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReadinessDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
